import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import type { TaskImportance } from '@map/shared';
import { createTask, fetchUniverse } from './api.js';
import { MarkdownEditor } from './MarkdownEditor.js';
import { useIsMobile } from '../lib/useIsMobile.js';

/**
 * Capture, as a form.
 *
 * One line was fast and told the system nothing: every rock came out the same
 * size because there was nothing to size it by. A task says what it is, how
 * much it matters and when it is wanted, and the rock that appears is shaped
 * by all three — so the sky is worth looking at rather than a field of
 * identical pebbles. `c` still opens it and Cmd/Ctrl+Enter still files it, so
 * the keyboard path is as short as it ever was.
 */
export const IMPORTANCE: Array<{ value: TaskImportance; label: string; hint: string }> = [
  { value: 'p1', label: 'P1', hint: 'Must not be missed' },
  { value: 'p2', label: 'P2', hint: 'Matters' },
  { value: 'p3', label: 'P3', hint: 'Would be good' },
];

export const URGENCY: Array<{ value: 'none' | 'soon' | 'urgent'; label: string; hint: string }> = [
  { value: 'none', label: 'Whenever', hint: 'No pressure on it' },
  { value: 'soon', label: 'Soon', hint: 'Wants doing this week' },
  { value: 'urgent', label: 'ASAP', hint: 'Pressing' },
];

export function CaptureForm() {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [importance, setImportance] = useState<TaskImportance>('p2');
  const [urgency, setUrgency] = useState<'none' | 'soon' | 'urgent'>('none');
  const [due, setDue] = useState('');
  const [planetId, setPlanetId] = useState<string | null>(null);
  const [confirmation, setConfirmation] = useState<string | null>(null);
  const first = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();
  const mobile = useIsMobile();

  const universe = useQuery({ queryKey: ['universe'], queryFn: fetchUniverse });
  const planets = (universe.data?.planets ?? []).filter((p) => !p.archived);

  const reset = () => {
    setTitle('');
    setBody('');
    setImportance('p2');
    setUrgency('none');
    setDue('');
    setPlanetId(null);
  };

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'c' && !event.metaKey && !event.ctrlKey && !isTyping(event.target)) {
        event.preventDefault();
        setOpen(true);
      }
      if (event.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (open) first.current?.focus();
  }, [open]);

  const submit = useMutation({
    mutationFn: () =>
      createTask({
        title: title.trim(),
        bodyMd: body.trim() ? body.trim() : null,
        importance,
        urgency,
        targetDate: due || null,
        planetId,
      }),
    onSuccess: async (task) => {
      setConfirmation(`Adrift: ${task.title}`);
      reset();
      setOpen(false);
      await queryClient.invalidateQueries({ queryKey: ['floating-tasks'] });
      window.setTimeout(() => setConfirmation(null), 4000);
    },
  });

  /**
   * What is stopping this being filed, if anything. A disabled button that
   * will not say why is a dead end: you press it, nothing happens, and the
   * missing answer is a grey line of text somewhere else on the form.
   */
  const missing = !title.trim() ? 'title' : !planetId ? 'planet' : null;
  const [shown, setShown] = useState<string | null>(null);

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (missing) {
      setShown(missing);
      if (missing === 'title') first.current?.focus();
      return;
    }
    submit.mutate();
  }

  const segment = (active: boolean) =>
    `rounded-[var(--radius-sm)] px-3 py-1.5 text-sm transition-colors ${
      active ? 'text-[var(--space-deep)]' : 'text-text-muted hover:text-text'
    }`;

  return (
    <>
      {open && (
        <div
          className="fixed inset-0 z-40 flex justify-center overflow-y-auto px-3 py-6 md:py-10"
          style={{ background: 'color-mix(in oklch, var(--space-deep) 62%, transparent)' }}
          onMouseDown={(e) => e.target === e.currentTarget && setOpen(false)}
        >
          <form
            onSubmit={onSubmit}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) onSubmit(e);
            }}
            className="h-fit w-full max-w-4xl rounded-[var(--radius-lg)] p-5"
            style={{
              background: 'var(--surface-1)',
              boxShadow: 'var(--shadow-panel)',
              ...(mobile ? { paddingBottom: 'calc(var(--safe-bottom) + 1rem)' } : {}),
            }}
          >
            <input
              ref={first}
              value={title}
              onChange={(e) => {
                setTitle(e.target.value);
                setShown(null);
              }}
              placeholder="Log a task"
              aria-label="Title"
              autoCapitalize="sentences"
              className="w-full bg-transparent px-1 py-1.5 text-lg outline-none"
            />

            <MarkdownEditor
              value={body}
              onChange={setBody}
              minHeight="18rem"
              placeholder="Anything worth writing down. Type markdown and it sets itself: # heading, - list, **bold**."
            />

            {/* Which planet this is closest to. Required, and asked before
                anything optional: it is where the task is kept, and the ocean
                it will sail once it is done. */}
            <label className="mt-3 flex flex-col gap-1.5">
              <span
                className="text-xs"
                style={{ color: shown === 'planet' ? 'var(--health-4)' : 'var(--text-muted)' }}
              >
                Closest planet
              </span>
              <div className="flex flex-wrap gap-1.5">
                {planets.map((planet) => (
                  <button
                    key={planet.id}
                    type="button"
                    onClick={() => {
                      setPlanetId(planet.id);
                      setShown(null);
                    }}
                    className={`rounded-[var(--radius-md)] px-3 py-1.5 text-sm ${
                      planetId === planet.id ? 'text-[var(--space-deep)]' : 'text-text-muted hover:text-text'
                    }`}
                    style={{
                      background: planetId === planet.id ? 'var(--accent)' : 'var(--surface-0)',
                    }}
                  >
                    {planet.name}
                  </button>
                ))}
                {planets.length === 0 && (
                  <span className="text-sm text-text-muted">No planets yet — set one up first.</span>
                )}
              </div>
            </label>

            <div className="mt-3 grid gap-3 md:grid-cols-3">
              <label className="flex flex-col gap-1.5">
                <span className="text-xs text-text-muted">Importance</span>
                <div
                  className="flex gap-1 rounded-[var(--radius-md)] p-1"
                  style={{ background: 'var(--surface-0)' }}
                >
                  {IMPORTANCE.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      title={option.hint}
                      onClick={() => setImportance(option.value)}
                      className={`flex-1 ${segment(importance === option.value)}`}
                      style={importance === option.value ? { background: 'var(--accent)' } : undefined}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </label>

              <label className="flex flex-col gap-1.5">
                <span className="text-xs text-text-muted">Urgency</span>
                <div
                  className="flex gap-1 rounded-[var(--radius-md)] p-1"
                  style={{ background: 'var(--surface-0)' }}
                >
                  {URGENCY.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      title={option.hint}
                      onClick={() => setUrgency(option.value)}
                      className={`flex-1 ${segment(urgency === option.value)}`}
                      style={urgency === option.value ? { background: 'var(--accent)' } : undefined}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </label>

              <label className="flex flex-col gap-1.5">
                <span className="text-xs text-text-muted">Due</span>
                <div className="flex items-center gap-2">
                  <input
                    type="date"
                    value={due}
                    onChange={(e) => setDue(e.target.value)}
                    aria-label="Due date"
                    className="min-w-0 flex-1 rounded-[var(--radius-md)] px-3 py-2 text-sm outline-none"
                    style={{ background: 'var(--surface-0)', color: due ? 'var(--text)' : 'var(--text-muted)' }}
                  />
                  {/* Having no date is the default and a real answer, not a
                      field left blank by accident. */}
                  {due && (
                    <button
                      type="button"
                      onClick={() => setDue('')}
                      className="shrink-0 rounded-[var(--radius-sm)] px-2 py-1 text-xs text-text-muted hover:text-text"
                    >
                      Clear
                    </button>
                  )}
                </div>
              </label>
            </div>

            <div className="mt-4 flex items-center gap-2">
              <span
                className="text-xs"
                style={{ color: shown && missing ? 'var(--health-4)' : 'var(--text-muted)' }}
              >
                {shown && missing === 'title'
                  ? 'It needs a title'
                  : shown && missing === 'planet'
                    ? 'Pick the planet it is closest to'
                    : due
                      ? ''
                      : 'No due date'}
                {submit.isError ? ' · could not save that' : ''}
              </span>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="ml-auto rounded-[var(--radius-md)] px-3 py-2 text-sm text-text-muted hover:text-text"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submit.isPending}
                className="rounded-[var(--radius-md)] px-4 py-2 text-sm font-medium disabled:opacity-40"
                style={{ background: 'var(--accent)', color: 'var(--space-deep)' }}
              >
                {submit.isPending ? 'Sending up…' : 'Done'}
              </button>
            </div>
          </form>
        </div>
      )}

      {confirmation && (
        <div
          role="status"
          className={
            mobile
              ? 'fixed inset-x-3 z-40 rounded-[var(--radius-md)] px-4 py-3 text-sm'
              : 'fixed bottom-6 left-1/2 z-40 -translate-x-1/2 rounded-[var(--radius-md)] px-4 py-2 text-sm'
          }
          style={{
            background: 'var(--surface-2)',
            boxShadow: 'var(--shadow-raised)',
            ...(mobile ? { bottom: 'calc(var(--safe-bottom) + 9.75rem)' } : {}),
          }}
        >
          {confirmation}
        </div>
      )}

      {!open && (
        <button
          onClick={() => setOpen(true)}
          aria-label="Capture a task"
          className={
            mobile
              ? 'tap fixed right-4 z-30 flex h-14 w-14 items-center justify-center rounded-full text-2xl'
              : 'fixed bottom-6 right-6 z-30 rounded-full px-4 py-2 text-sm'
          }
          style={{
            background: mobile ? 'var(--accent)' : 'var(--surface-2)',
            color: mobile ? 'var(--space-deep)' : undefined,
            boxShadow: 'var(--shadow-raised)',
            ...(mobile ? { bottom: 'calc(var(--safe-bottom) + 5rem)' } : {}),
          }}
        >
          {mobile ? '+' : (
            <>
              Capture <kbd className="ml-1 text-text-muted">c</kbd>
            </>
          )}
        </button>
      )}
    </>
  );
}

function isTyping(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable;
}
